import { useEffect } from 'react';

import { Button } from '@/components/Button/Button';
import { PracticeQuestionDisplay } from '@/features/ai-homework/PracticeQuestionDisplay';
import type { QuestionAIState } from '@/features/ai-homework/aiHomeworkTypes';

import styles from './QuestionInfoPanelContent.module.css';

interface PracticeTabContentProps {
  onRequestAnotherPractice: () => void;
  onRequestPractice: () => void;
  onSelectAnswer: (index: number) => void;
  onVotePractice: (vote: 'up' | 'down') => void;
  practice: QuestionAIState['practice'];
  showPracticeButton: boolean;
}

export function PracticeTabContent({
  onRequestAnotherPractice,
  onRequestPractice,
  onSelectAnswer,
  onVotePractice,
  practice,
  showPracticeButton,
}: PracticeTabContentProps) {
  const shouldAutoRequestPractice =
    showPracticeButton &&
    practice.question === null &&
    !practice.isLoading &&
    !practice.isRegenerating &&
    !practice.error &&
    practice.statusMessage === null;
  const showPracticeRetry =
    showPracticeButton &&
    Boolean(practice.error) &&
    !practice.isLoading &&
    !practice.isRegenerating &&
    practice.question === null;

  useEffect(() => {
    if (shouldAutoRequestPractice) {
      void onRequestPractice();
    }
  }, [onRequestPractice, shouldAutoRequestPractice]);

  return (
    <div className={styles.stack}>
      <PracticeQuestionDisplay
        error={practice.error}
        isAnswerRevealed={practice.isAnswerRevealed}
        isLoading={practice.isLoading}
        isRegenerating={practice.isRegenerating}
        onDownvote={() => onVotePractice('down')}
        onRequestAnother={onRequestAnotherPractice}
        onSelectAnswer={onSelectAnswer}
        onUpvote={() => onVotePractice('up')}
        question={practice.question}
        response={practice.response}
        selectedAnswer={practice.selectedAnswer}
        statusMessage={practice.statusMessage}
        vote={practice.vote}
      />
      {showPracticeRetry ? (
        <Button
          className={styles.retryButton}
          onClick={onRequestPractice}
          size="small"
          variant="secondary"
        >
          Try again
        </Button>
      ) : null}
    </div>
  );
}
